import clsx from 'clsx'
import React, { useEffect } from 'react'

import ScrollArea from '@/components-latest/primitives/Scrollable'
import { textVariants } from '@/components-latest/primitives/Text'
import { PiledToast } from '@/components-latest/ui/Toaster/PiledToast/PiledToast'
import {
  GAP,
  TOAST_WIDTH,
  VIEWPORT_OFFSET,
  VISIBLE_TOASTS_AMOUNT
} from '@/components-latest/ui/Toaster/Toaster.constants'
import { HeightT } from '@/components-latest/ui/Toaster/Toaster.types'
import { INotification } from '@/ducks/notifications/slices/notification.slice'

import Styles from '../Toaster.module.scss'

interface IPileProps {
  toasts: INotification[]
  offset: string
  visibleToasts: number
  gap: number
  setFrontHeight: React.Dispatch<React.SetStateAction<number>>
  systemToastsLength: number
  setExpanded: React.Dispatch<React.SetStateAction<boolean>>
  expanded: boolean
  onToastRemove: (toast: INotification) => void
}

const Pile: React.FC<IPileProps> = props => {
  const {
    toasts,
    offset,
    visibleToasts = VISIBLE_TOASTS_AMOUNT,
    gap = GAP,
    setFrontHeight,
    systemToastsLength,
    expanded,
    setExpanded,
    onToastRemove
  } = props

  const [heights, setHeights] = React.useState<HeightT[]>([])

  const viewportRef = React.useRef<HTMLDivElement>(null)
  const listRef = React.useRef<HTMLUListElement>(null)

  const lastFocusedElementRef = React.useRef<HTMLElement | null>(null)
  const isFocusWithinRef = React.useRef(false)
  const collapseRef = React.useRef<HTMLButtonElement>(null)

  // calculate expanded pile height;
  const listHeight = React.useMemo(() => {
    const itemsHeight = heights.reduce((prev, curr) => {
      return prev + curr.height
    }, 0)

    return (heights.length - 1) * gap + itemsHeight
  }, [gap, heights])

  useEffect(() => {
    setFrontHeight(heights[0]?.height || 0)
  }, [heights, setFrontHeight])

  React.useEffect(() => {
    if (toasts.length <= 1) {
      setExpanded(false)
    }
  }, [toasts])

  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (
        event.code === 'Escape' &&
        (document.activeElement === listRef.current ||
          listRef.current?.contains(document.activeElement))
      ) {
        setExpanded(false)
      }
    }
    document.addEventListener('keydown', handleKeyDown)

    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [])

  React.useEffect(() => {
    if (listRef.current) {
      return () => {
        if (lastFocusedElementRef.current) {
          lastFocusedElementRef.current.focus({ preventScroll: true })
          lastFocusedElementRef.current = null
          isFocusWithinRef.current = false
        }
      }
    }
  }, [listRef.current])

  const prevExpanded = React.useRef(false)

  useEffect(() => {
    if (viewportRef.current) {
      viewportRef.current.scrollTo({
        top: listHeight,
        behavior: expanded ? 'auto' : 'smooth'
      })
    }

    return () => {
      prevExpanded.current = expanded
    }
  }, [expanded, listHeight])

  if (!toasts.length) return null

  return (
    <li
      className={Styles.pileContainer}
      data-expanded={expanded}
      style={
        {
          '--front-toast-height': `${heights[0]?.height || 0}px`,
          '--offset':
            typeof offset === 'number'
              ? `${offset}px`
              : offset || VIEWPORT_OFFSET,
          '--width': `${TOAST_WIDTH}px`,
          '--gap': `${gap}px`,
          '--list-height': `${listHeight}px`,
          '--z-index': systemToastsLength
        } as React.CSSProperties
      }>
      <ul
        ref={listRef}
        tabIndex={-1}
        className={Styles.pileList}
        data-expanded={expanded}>
        {toasts.map((toast, index) => (
          <PiledToast
            key={toast.id}
            index={index}
            toast={toast}
            visibleToasts={visibleToasts}
            toasts={toasts}
            heights={heights}
            setHeights={setHeights}
            gap={gap}
            expanded={expanded}
            systemToastsLength={systemToastsLength}
            onToastRemove={onToastRemove}
          />
        ))}
      </ul>
      <button
        onClick={() => setExpanded(false)}
        ref={collapseRef}
        className={clsx(
          Styles.collapseButton,
          textVariants({ variant: 'nextBody', weight: 'semiBold' })
        )}
        style={{ '--z-index': toasts.length } as React.CSSProperties}>
        Collapse
      </button>
      <ScrollArea
        variant="notification"
        scrollbarClassName={Styles.scrollbar}
        scrollHideDelay={0}
        className={Styles.scrollArea}
        viewportRef={viewportRef}
        viewportClassName={Styles.scrollViewport}>
        {expanded && (
          <div className={Styles.overlay} onClick={() => setExpanded(false)} />
        )}
        <ul
          ref={listRef}
          tabIndex={-1}
          className={Styles.pileList}
          data-expanded={expanded}>
          {toasts.map((toast, index) => (
            <PiledToast
              key={toast.id}
              index={index}
              toast={toast}
              visibleToasts={visibleToasts}
              toasts={toasts}
              heights={heights}
              setHeights={setHeights}
              gap={gap}
              expanded={expanded}
              systemToastsLength={systemToastsLength}
              onToastRemove={onToastRemove}
              shallow
            />
          ))}
        </ul>
      </ScrollArea>
      {!expanded && toasts.length > 1 && (
        <div
          style={
            {
              '--z-index': toasts.length + systemToastsLength
            } as React.CSSProperties
          }
          className={Styles.interactArea}
          onClick={() => toasts.length > 1 && setExpanded(true)}
        />
      )}
    </li>
  )
}

export { Pile }
